'use memo';

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useBusinessPartnerDetail } from '@/components/Combobox/BusinessPartnerCombobox/-query';
import Icon from '@/components/Icon/Icon';
import { LabelXBase } from '@/components/LabelX';
import {
  BusinessPartnerSelect,
  type BusinessPartner,
} from '@/components/Select/BusinessPartnerSelect';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange?: (
    businessPartnerId?: string,
    businessPartner?: BusinessPartner,
  ) => void;
  inputWidth?: number | string;
  /**
   * 読み取り表示時に取引先詳細への外部リンク（別タブ）を表示する。
   * 有効時はアイコンが建物アイコンから外部リンクアイコンに変わる。
   */
  showDetailLink?: boolean;
};

/**
 * Card-based Form / BusinessPartnerSelectItem
 * 取引先 (BusinessPartner) ID 参照属性のための CForm アイテム。
 * Section/Attribute SDUI の `valueType: "BP_SELECT"` を扱う。
 */
export const CFormBpSelectItem = buildCFormItem<Props>((props: Props) => {
  // 選択中の取引先情報を取得（ID のみ受け取るため、表示名は別途解決する）
  const { data: selected } = useBusinessPartnerDetail(props.value ?? null);

  const selectedBp: BusinessPartner | undefined = selected
    ? { id: selected.businessPartnerId, name: selected.businessPartnerName }
    : undefined;

  const readComponent =
    props.readComponent ??
    (selectedBp ? (
      <LabelXBase label={selectedBp.name}>
        {props.showDetailLink ? (
          <Link
            to="/business-partner/$id"
            target="_blank"
            rel="noopener noreferrer"
            params={{ id: selectedBp.id }}
          >
            <Icon iconName="SquareArrowOutUpRightIcon" />
          </Link>
        ) : (
          <Icon iconName="Building2" className="shrink-0 w-6 h-6" />
        )}
      </LabelXBase>
    ) : null);

  return (
    <CFormItemBase
      {...props}
      readComponent={readComponent}
      writeComponent={
        <BusinessPartnerSelect
          disabled={props.readonly}
          allowEmpty={props.allowEmpty}
          value={selectedBp}
          onChange={(bp) => props.onChange?.(bp?.id, bp)}
          width={
            typeof props.inputWidth === 'number' ? props.inputWidth : undefined
          }
        />
      }
    />
  );
});
